import { jsonGet } from "../api";

/**
 * Detection samples — the frequent, cheap half of monitoring.
 *
 * Note the shape: brand and surface levels carry a rate with a confidence interval, but prompt
 * rows carry raw counts only. That is deliberate. A week of daily sampling is seven observations,
 * whose 95% interval is roughly ±30 points, so a per-prompt percentage would be inventing
 * precision. The API does not hand one over.
 */
export const fetchBrandMentions = async (brandId, { surface, queryId, from, to } = {}) => {
  const params = new URLSearchParams();
  if (surface) params.set("surface", surface);
  if (queryId) params.set("queryId", queryId);
  if (from) params.set("from", from.toISOString());
  if (to) params.set("to", to.toISOString());

  const qs = params.toString();
  return await jsonGet(`brands/${brandId}/mentions${qs ? `?${qs}` : ""}`);
};

/**
 * Every check taken for one prompt, newest first — the receipt behind its "4 / 28".
 *
 * Carries no answer text. An AI Mode answer is roughly 6 KB and a 90-day window holds a few
 * hundred checks, so the text is fetched one at a time by fetchSampleAnswer.
 */
export const fetchPromptSamples = async (brandId, queryId, { surface, from, to } = {}) => {
  const params = new URLSearchParams();
  if (surface) params.set("surface", surface);
  if (from) params.set("from", from.toISOString());
  if (to) params.set("to", to.toISOString());

  const qs = params.toString();
  return await jsonGet(
    `brands/${brandId}/mentions/prompts/${queryId}/samples${qs ? `?${qs}` : ""}`,
  );
};

/**
 * The answer text one check was taken from.
 *
 * Addressed by SAMPLE id: the client never sees a query-run id, so it cannot ask for one. Answers
 * are kept 90 days while the check itself is kept forever, so this 404s for old checks — the
 * `hasStoredAnswer` flag on the list says which ones in advance.
 */
export const fetchSampleAnswer = async (brandId, sampleId) =>
  await jsonGet(`brands/${brandId}/mentions/samples/${sampleId}/answer`);

/**
 * Spellings the analysis pipeline has already seen that look like this brand but are not yet
 * matched. Suggestions only — the backend never applies them, because an alias that matches a
 * competitor would credit their mentions to this brand permanently.
 */
export const fetchAliasSuggestions = async (brandId) =>
  await jsonGet(`brands/${brandId}/mentions/alias-suggestions`);

/**
 * What the AI surfaces actually read when answering this brand's unaided prompts.
 *
 * Ranked by how many answers cited each domain — one answer counts once however many pages on it
 * were linked, because an answer citing six YouTube videos leaned on YouTube once. The number to
 * act on is `withBrand`: a source read constantly whose answers never name you is where to go and
 * publish.
 */
export const fetchBrandSources = async (brandId, { days = 28, surface } = {}) => {
  const params = new URLSearchParams({ days: String(days) });
  if (surface) params.set("surface", surface);
  return await jsonGet(`brands/${brandId}/mentions/sources?${params}`);
};
